// ── Client-side task_group_label helpers (C1234) ──
//
// Mirrors api/src/lib/task-group-labels.js (CommonJS, server-only — SPA can't import it).
// Stored value IS the plural English label (project.task_group_label). No i18n layer in
// this SPA (see lib/statuses.js header) — renders as-is.

export const TASK_GROUP_LABELS = ['Sprints', 'Batches', 'Bags', 'Jars', 'Groups', 'Steps'];
export const DEFAULT_TASK_GROUP_LABEL = 'Batches';

const SINGULAR = {
  Sprints: 'Sprint',
  Batches: 'Batch',
  Bags: 'Bag',
  Jars: 'Jar',
  Groups: 'Group',
  Steps: 'Step',
};

// Unknown/unset value → default plural.
export function groupLabelPlural(value) {
  return TASK_GROUP_LABELS.includes(value) ? value : DEFAULT_TASK_GROUP_LABEL;
}

export function groupLabelSingular(value) {
  return SINGULAR[groupLabelPlural(value)];
}

// ── Boilerplate sprint-name detection (C1395) ──
//
// Every auto-created sprint row (api/src/routes/sprints.js's default name, the Task App's
// sprint-assign.js/mcp/server.js/cli/migrate-tasks.js) writes the literal name
// `Sprint ${n}` — never the project's own noun. Pre-C1395, sprint-board.js's renderLane()
// and board-filters.js's sprintLabel() treated ANY stored name as custom and rendered it
// verbatim (worse, sprintLabel() appended it: "Batch 12 — Sprint 12"), so a group whose row
// happened to exist (nearly always, once created) kept showing "Sprint N" regardless of
// task_group_label. A boilerplate name isn't a custom name: strip it, fall back to the
// live noun. No i18n layer in this SPA (see the header comment), so the alternation is
// just the six English singular nouns.
const _escapeRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const _BOILERPLATE_NOUNS = Object.values(SINGULAR); // ['Sprint', 'Batch', 'Bag', 'Jar', 'Group', 'Step']
const _BOILERPLATE_RE = new RegExp(`^\\s*(?:${_BOILERPLATE_NOUNS.map(_escapeRe).join('|')})\\s*#?\\s*(\\d+)\\s*$`, 'i');

// True when `name` is exactly an auto-generated "<noun> <n>" name for THIS n — a genuinely
// custom name (including one that merely leads with a noun, e.g. "Sprint 5 — Auth
// revamp") or a mismatched number returns false.
export function isGeneratedGroupName(name, n) {
  if (!name) return false;
  const m = _BOILERPLATE_RE.exec(name);
  return !!m && Number(m[1]) === n;
}

// Group container title: "<Singular> <n>", or "<Singular> <n> — <name>" when the sprint
// row carries a genuinely custom name. A boilerplate stored name (see isGeneratedGroupName)
// is treated as absent, not as a custom name to append. `singular` is the already-resolved
// noun (groupLabelSingular(project.task_group_label)) — every call site already has it
// computed, so this takes the noun directly rather than the raw stored label value.
// Matches board-filters.js's pre-existing sprintLabel() contract (always noun+number,
// customer name appended) — see groupLaneTitle() below for sprint-board.js's different one.
export function groupContainerTitle(n, rec, singular = 'Sprint') {
  const title = `${singular} ${n}`;
  if (!rec?.name || isGeneratedGroupName(rec.name, n)) return title;
  return `${title} — ${rec.name}`;
}

// Sprint-board.js lane title: unlike groupContainerTitle() above, a genuinely custom name
// wins OUTRIGHT (no noun+number prefix) — only a missing or boilerplate name falls back to
// "<Singular> <n>". Preserves that page's pre-C1395 contract (see
// tt-web-sprint.md § Group label) while still fixing the boilerplate-name bug.
export function groupLaneTitle(n, rec, singular = 'Sprint') {
  if (rec?.name && !isGeneratedGroupName(rec.name, n)) return rec.name;
  return `${singular} ${n}`;
}
